import { CircularProgress, Grid, MenuItem, TextField } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { apiGet, apiPut } from '../../generic/Api_Functions'
import { useNavigate } from 'react-router'
import ButtonBackSave from '../../generic/buttons/floatingButtons/ButtonBackSave'
import { DefaultAccordion } from '../../generic/DefaultAccordion'
import { SmartSaveDialog, SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import AlertUI from '../../generic/AlertUI'
import { SmartSelect } from '../../generic/smartSelect'
import UseCaseSection from './UseCaseSection'
import SmartSortableContainer from '../../generic/smartSortable/SmartSortableContainer'
import DefaultSortableRenderItem from '../../generic/smartSortable/GenericSortableRenderItem'

/*
 *   Último acto de servicio de Markel Lomana
 *
 *        o7
 *       /|
 *       / \
 *
 */

/**
 * @typedef {import('../../generic/utilities/objectStructures/deviceGroup/DeviceGroup').DeviceGroup} DeviceGroup
 * @typedef {import('../../generic/utilities/objectStructures/globalStructures').IdName} IdName
 * @typedef {import('../../generic/utilities/objectStructures/globalStructures').IdCode} IdCode
 */

// A member may arrive without a position (data written before Sun numbered additions, or by another
// client). It is still shown and can still be dragged.
const hasPosition = (member) => Number.isFinite(member.positionInLine)

// Display order of the assembly line: by position ascending; members without position go last. Ties (legacy
// data with repeated positions) and the members without position are ordered by asset id.
const byDisplayOrder = (a, b) => {
  if (hasPosition(a) && hasPosition(b) && a.positionInLine !== b.positionInLine) {
    return a.positionInLine - b.positionInLine
  }
  if (hasPosition(a) !== hasPosition(b)) {
    return hasPosition(a) ? -1 : 1
  }
  return a.device.id - b.device.id
}

/**
 * DeviceGroupConfiguration component. Renders the configuration form for a device group, allowing the user to view
 * and edit its general properties (code, name, description, devices, type, mode) and, depending on the group type,
 * its use case functions (CELL) or assembly line positions (LINE).
 * @param {Object} props Component props
 * @param {DeviceGroup} props.deviceGroup Current device group being configured
 * @param {import('react').Dispatch<import('react').SetStateAction<DeviceGroup>>} props.setDeviceGroup React state setter to update the device group in the parent component
 * @returns {JSX.Element}
 */
const DeviceGroupConfiguration = (props) => {
  const { deviceGroup, setDeviceGroup } = props
  const [loaded, setLoaded] = useState(false)
  const [types, setTypes] = useState([])
  const [modes, setModes] = useState([])
  const [selectableDevices, setSelectableDevices] = useState([])
  const backSaveButtonRef = useRef()
  const [editMode, setEditMode] = useState(false)
  const [showSaveConfirm, setShowSaveConfirm] = useState(false)
  const [showSaveErrorModal, setShowSaveErrorModal] = useState(false)
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      const groupTypes = await apiGet('deviceGroupTypes')
      setTypes(groupTypes)
      const groupModes = await apiGet('deviceGroups/modes')
      setModes(groupModes)
      const devices = await apiGet('devicesBasicData')
      setSelectableDevices(devices)
    }
    if (!loaded) {
      fetchData()
      setLoaded(true)
    }
  }, [loaded])

  const goBack = (state) => {
    navigate('/configuration/assetGroups', { replace: true, state })
  }

  const updateDevice = () => {
    const copy = { ...deviceGroup }

    apiPut('assetGroupConfiguration/update', copy)
      .then(() => {
        setEditMode(false)
        goBack('EDIT')
        showAlert({
          title: t_('Device group updated successfully.'),
          severity: 'success'
        })
      })
      .catch((error) => {
        showAlert({
          title: '',
          message: error.message,
          severity: 'error'
        })
        backSaveButtonRef.current.showErrorAlert({
          title: t_('An error occurred while editing.'),
          message: error.message,
          error: 'error'
        })
      })
    setShowSaveConfirm(false)
  }

  const DeviceGroupDeviceItem = ({ item }) => {
    const labels = hasPosition(item) ? ['Position: ' + item.positionInLine, item?.device?.name] : [item?.device?.name]
    return <DefaultSortableRenderItem labels={labels} />
  }

  const onSortChanged = (items) => {
    items.forEach((item, index) => {
      item.positionInLine = index + 1
    })
    setDeviceGroup({ ...deviceGroup, deviceGroupDevices: items })
  }

  // Sun sends `[]` for a group without members, but an older Sun sends `null` and an external client may
  // omit the field: the screen must open in every case.
  const members = deviceGroup.deviceGroupDevices ?? []
  const sorted = [...members].sort(byDisplayOrder)
  // The asset, not its position, identifies an item: positions may be missing. The container sorts by
  // `itemOrder` (by the id when none is given), so it gets each member's rank in the display order.
  const displayRank = new Map(sorted.map((member, rank) => [member.device.id, rank]))

  const printDeviceGroup = () => {
    return (
      <>
        <SmartSaveDialog
          show={showSaveConfirm}
          cancelCallback={() => { setShowSaveConfirm(false) }}
          saveCallback={updateDevice}
        />
        <SmartDialog
          setOpen={showSaveErrorModal}
          title={createDialogTitle({ color: 'error', title: t_('Error saving'), icon: WarningAmberIcon })}
          // message={showError ? t_(errorMessage) : 'There is a parameter without a unit assigned'}
          cancel={false}
          accept={false}
          close
          closeCallback={() => { setShowSaveErrorModal(false) }}
        />
        <ButtonBackSave
          editControl={setEditMode}
          backButtonAction={() => goBack('BACK')}
          saveButtonAction={() => setShowSaveConfirm(true)}
          ref={backSaveButtonRef}
        />
        {AlertElement}
        <DefaultAccordion title={t_('GENERAL PROPERTIES')}>
          <Grid container direction='row' spacing={2} columns={{ xs: 1, sm: 4, md: 8, lg: 12 }}>
            <Grid item xs={2}>
              <TextField
                fullWidth
                label={t_('Code')}
                id='deviceCode'
                required
                value={deviceGroup.code}
                onChange={(event) => {
                  setDeviceGroup({ ...deviceGroup, code: event.target.value })
                }}
                disabled={!editMode}
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                fullWidth
                label={t_('Name')}
                id='deviceName'
                required
                value={deviceGroup.name}
                onChange={(event) => {
                  setDeviceGroup({ ...deviceGroup, name: event.target.value })
                }}
                disabled={!editMode}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label={t_('Description')}
                id='description'
                value={deviceGroup.description}
                onChange={(event) => {
                  setDeviceGroup({ ...deviceGroup, description: event.target.value })
                }}
                disabled={!editMode}
              />
            </Grid>
            <Grid item xs={3}>
              <SmartSelect
                label={t_('Assets')}
                disabled={!editMode}
                multiple
                required
                selectableOptions={selectableDevices.map(device => device.name)}
                value={deviceGroup.devices ? deviceGroup.devices.map(device => device.name) : []}
                onChange={(event) => {
                  const selectedDevices = selectableDevices.filter(device => event.includes(device.name))
                  setDeviceGroup({ ...deviceGroup, devices: selectedDevices })
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                fullWidth
                select
                id='groupType'
                label={t_('Type')}
                required
                value={types.length > 0 ? deviceGroup.deviceGroupType?.code : ''}
                onChange={(event) => {
                  const selectedType = types.find(type => type.code === event.target.value)
                  setDeviceGroup({ ...deviceGroup, deviceGroupType: selectedType })
                }}
                disabled={!editMode}
              >
                {types.map((type, index) => (
                  <MenuItem key={type.code + index} value={type.code}> {systemValueLabel(t_, 'device_group_type', type.code)} </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={2}>
              <TextField
                fullWidth
                select
                id='mode'
                label={t_('Mode')}
                value={modes.length > 0 ? deviceGroup.deviceGroupMode : ''}
                onChange={(event) => {
                  setDeviceGroup({ ...deviceGroup, deviceGroupMode: event.target.value })
                }}
                disabled={!editMode}
              >
                {modes.map((type, index) => (
                  <MenuItem key={type + index} value={type}> {systemValueLabel(t_, 'DeviceGroupMode', type)} </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DefaultAccordion>
        <DefaultAccordion
          title={t_('USE CASE FUNCTIONS')}
          disabled={deviceGroup.deviceGroupType?.code !== 'CELL'}
        >
          <UseCaseSection
            useCaseFunctions={deviceGroup.useCaseFunctions}
            setUseCaseFunctions={(useCaseFunctions) => setDeviceGroup((prev) => ({ ...prev, useCaseFunctions }))}
            editMode={editMode}
          />
        </DefaultAccordion>
        <DefaultAccordion
          title={t_('Positions on assembly line')}
          disabled={deviceGroup.deviceGroupType?.code !== 'LINE'}
        >
          <div style={{ width: '100%', margin: 'auto' }}>
            <SmartSortableContainer itemList={sorted} itemId={(member) => member.device.id} itemOrder={(member) => displayRank.get(member.device.id)} renderItem={DeviceGroupDeviceItem} onSortChanged={onSortChanged} disabled={!editMode} />
          </div>
        </DefaultAccordion>
      </>
    )
  }

  return (
    <>
      {deviceGroup?.id ? printDeviceGroup() : <CircularProgress />}
    </>
  )
}

export default DeviceGroupConfiguration
